import React, { useEffect, useState } from 'react';
import Modal from '../../../components/common/modal';
import { ClientSingleSelectWithSearch } from '../../../components/content-library/filter-controls/ClientSingleSelectWithSearch';
import { useClient } from '../../../hooks/useClients';

interface CopyClientResourcesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (sourceClientId: number) => Promise<void>;
  currentClientId: number;
  currentClientName: string;
  isSubmitting?: boolean;
}

const CopyClientResourcesModal: React.FC<CopyClientResourcesModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentClientId,
  currentClientName,
  isSubmitting = false,
}) => {
  const [sourceClientId, setSourceClientId] = useState<number | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSourceClientId(null);
      setShowConfirmation(false);
      setErrorMessage(null);
    }
  }, [isOpen]);

  const { data: sourceClient } = useClient(sourceClientId ?? undefined);

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    setSourceClientId(null);
    setShowConfirmation(false);
    setErrorMessage(null);
    onClose();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!sourceClientId || isSubmitting) {
      return;
    }

    if (!showConfirmation) {
      setErrorMessage(null);
      setShowConfirmation(true);
      return;
    }

    try {
      await onConfirm(sourceClientId);
      setSourceClientId(null);
      setShowConfirmation(false);
      setErrorMessage(null);
    } catch (error: any) {
      const message =
        error?.response?.data?.description ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to copy client resources';
      setErrorMessage(message);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isModel={true}
      InnerComponent={
        <div
          className="inline-block w-full transform overflow-visible rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:max-w-lg sm:align-middle"
          onClick={(event) => event.stopPropagation()}
        >
          <form onSubmit={handleSubmit}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">Copy Client Resources</h3>
              <button
                type="button"
                className="text-blue-primary text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Close
              </button>
            </div>
            <div className="space-y-4 px-6 py-5">
              {!showConfirmation ? (
                <>
                  <p className="text-sm text-gray-600">
                    Copy resource configuration into <strong>{currentClientName}</strong> from another client. This
                    replaces the current client&apos;s resource selection and client-specific resources.
                  </p>
                  <ClientSingleSelectWithSearch
                    label="Source Client"
                    selectedClientId={sourceClientId}
                    onChange={setSourceClientId}
                    placeholder="Select a client"
                    showClearOption={false}
                    excludeClientIds={[currentClientId]}
                    disabled={isSubmitting}
                  />
                </>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-red-600">
                    This will overwrite the current resource configuration for {currentClientName}.
                  </p>
                  <p className="text-sm text-gray-600">
                    Copy from <strong>{sourceClient?.name ?? `Client #${sourceClientId}`}</strong> including global
                    resource selection and all client-specific resources?
                  </p>
                </div>
              )}
              {errorMessage && (
                <p className="text-sm text-red-600" data-testid="copy-client-error">
                  {errorMessage}
                </p>
              )}
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              {showConfirmation && (
                <button
                  type="button"
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 disabled:opacity-50"
                  onClick={() => setShowConfirmation(false)}
                  disabled={isSubmitting}
                >
                  Back
                </button>
              )}
              <button
                type="submit"
                className="bg-blue-primary rounded-md px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!sourceClientId || isSubmitting}
                data-testid="copy-client-confirm-button"
              >
                {isSubmitting ? 'Copying…' : showConfirmation ? 'Confirm Copy' : 'Continue'}
              </button>
            </div>
          </form>
        </div>
      }
      onClose={handleClose}
    />
  );
};

export default CopyClientResourcesModal;
